import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  FaCalendarAlt,
  FaClock,
  FaFileInvoice,
  FaPills,
  FaPhoneAlt,
  FaPrescription,
  FaSearch,
  FaUserInjured,
  FaUserMd,
  FaUsers,
} from "react-icons/fa";
import { MdEventNote, MdLocalPharmacy } from "react-icons/md";
import { IoPeopleSharp } from "react-icons/io5";
import { Bar, Pie } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { useNavigate } from "react-router-dom";
import Apiurl from "../environment/Apiurl";
import "../../assets/css/Dashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const COLORS = {
  teal: "#01AEBE",
  green: "#79C143",
  blue: "#4B91F0",
  orange: "#F5A623",
  purple: "#7B61FF",
  red: "#E45757",
  dark: "#17324D",
  muted: "#6B7A90",
  bg: "#F4F8FA",
};

const ROLE = {
  MANAGEMENT: "Management",
  SUPER_ADMIN: "Super Admin",
  RECEPTIONIST: "Receptionist",
  DOCTOR: "Doctor",
  PHARMACIST: "Pharmacist",
};

const formatTo12Hour = (time24) => {
  if (!time24) return "N/A";
  const parts = String(time24).split(":");
  if (parts.length < 2) return time24;
  let hour = Number(parts[0]);
  const minutes = parts[1];
  if (Number.isNaN(hour)) return time24;
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour}:${minutes} ${ampm}`;
};

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = /^\d{4}-\d{2}-\d{2}$/.test(String(value))
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getLocalDateString = (offset = 0) => {
  const date = new Date();
  date.setDate(date.getDate() + offset);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getPatient = (item) =>
  item?.patient_details ||
  item?.patients_details?.[0] ||
  item?.patient ||
  {};

const getDoctor = (item) =>
  item?.doctor_details ||
  item?.doctors_details?.[0] ||
  item?.doctor ||
  {};

const getPatientName = (item) => {
  const patient = getPatient(item);
  return (
    `${patient.first_name || ""} ${patient.last_name || ""}`.trim() ||
    patient.patient_name ||
    "N/A"
  );
};

const getPatientId = (item) => {
  const patient = getPatient(item);
  return patient.patient_id || item?.patient_id || "N/A";
};

const getDoctorName = (item) => {
  const doctor = getDoctor(item);
  return doctor.doctor_name || doctor.name || item?.doctor_name || "N/A";
};

const getAuthConfig = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
  },
});

const normaliseArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.appointments)) return value.appointments;
  if (Array.isArray(value?.records)) return value.records;
  return [];
};

const safeNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const StatsCard = ({
  icon: Icon,
  title,
  value,
  subtitle,
  color = COLORS.teal,
  onClick,
  buttonText,
}) => (
  <div className="col-12 col-sm-6 col-xl-3">
    <div
      className="card border-0 h-100 dashboard-kpi-card dashboard-kpi-modern"
      style={{
        "--kpi-color": color,
      }}
    >
      <div className="card-body p-3 position-relative">
        <div
          className="dashboard-kpi-accent"
          style={{ background: color }}
        />

        <div className="d-flex justify-content-between align-items-start gap-3">
          <div className="flex-grow-1">
            <div
              className="fw-semibold text-uppercase mb-2"
              style={{
                color: COLORS.muted,
                fontSize: 10,
                letterSpacing: ".7px",
              }}
            >
              {title}
            </div>

            <div
              className="fw-bold"
              style={{
                color: COLORS.dark,
                fontSize: 27,
                lineHeight: 1.1,
              }}
            >
              {value}
            </div>

            <div
              className="mt-2 small"
              style={{
                color: COLORS.muted,
                minHeight: 20,
                fontSize: 11,
              }}
            >
              {subtitle}
            </div>
          </div>

          <div
            className="dashboard-kpi-icon"
            style={{
              background: `${color}14`,
              color,
              border: `1px solid ${color}25`,
            }}
          >
            <Icon size={22} />
          </div>
        </div>

        {onClick && buttonText ? (
          <button
            type="button"
            className="btn btn-sm w-100 mt-2"
            style={{
              background: color,
              color: "#fff",
              border: 0,
              borderRadius: 9,
            }}
            onClick={onClick}
          >
            {buttonText}
          </button>
        ) : null}
      </div>
    </div>
  </div>
);

const BillingSummaryCard = ({ billingData }) => {
  const op = safeNumber(billingData?.opBilling);
  const pharmacy = safeNumber(billingData?.pharmacyBilling);
  const total = op + pharmacy;

  return (
    <div className="col-12 col-sm-6 col-xl-3">
      <div
        className="card border-0 h-100 dashboard-kpi-card dashboard-kpi-modern"
        style={{
          "--kpi-color": COLORS.teal,
        }}
      >
        <div className="card-body p-3 position-relative">
          <div
            className="dashboard-kpi-accent"
            style={{ background: COLORS.teal }}
          />

          <div className="d-flex justify-content-between align-items-start gap-3">
            <div className="flex-grow-1">
              <div
                className="fw-semibold text-uppercase mb-2"
                style={{
                  color: COLORS.muted,
                  fontSize: 10,
                  letterSpacing: ".7px",
                }}
              >
                Overall Billing
              </div>

              <div
                className="fw-bold"
                style={{
                  color: COLORS.dark,
                  fontSize: 27,
                  lineHeight: 1.1,
                }}
              >
                {formatMoney(total)}
              </div>

              <div className="d-flex gap-2 mt-2">
                <span
                  className="dashboard-kpi-chip"
                  style={{
                    color: COLORS.blue,
                    background: `${COLORS.blue}10`,
                  }}
                >
                  O/P {formatMoney(op)}
                </span>

                <span
                  className="dashboard-kpi-chip"
                  style={{
                    color: COLORS.green,
                    background: `${COLORS.green}12`,
                  }}
                >
                  Pharmacy {formatMoney(pharmacy)}
                </span>
              </div>
            </div>

            <div
              className="dashboard-kpi-icon"
              style={{
                background: `${COLORS.teal}14`,
                color: COLORS.teal,
                border: `1px solid ${COLORS.teal}25`,
              }}
            >
              <FaFileInvoice size={22} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const roleName = localStorage.getItem("role_name") || "";

  const isManagement =
    roleName === ROLE.MANAGEMENT || roleName === ROLE.SUPER_ADMIN;
  const isDoctor = roleName === ROLE.DOCTOR;
  const isReceptionist = roleName === ROLE.RECEPTIONIST;
  const isPharmacist = roleName === ROLE.PHARMACIST;
  const canSeeManagement = isManagement;
  const canSeePharmacy = isManagement || isPharmacist;
  const canSeeAppointments =
    isManagement || isReceptionist || isDoctor;

  const [appointmentData, setAppointmentData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [appointmentFilter, setAppointmentFilter] = useState("today");
  const [billingRange, setBillingRange] = useState("last7days");
  const [billingTab, setBillingTab] = useState("op");

  const [loading, setLoading] = useState({
    appointments: false,
    dashboard: false,
    billing: false,
    billingChart: false,
    followups: false,
    staff: false,
    stock: false,
    prescriptions: false,
  });

  const [stats, setStats] = useState({
    totalAppointments: 0,
    totalStaff: 0,
  });

  const [patientCount, setPatientCount] = useState(0);
  const [staffOnDuty, setStaffOnDuty] = useState([]);
  const [billingData, setBillingData] = useState({
    opBilling: 0,
    pharmacyBilling: 0,
    opBillCount: 0,
    pharmacyBillCount: 0,
  });
  const [billingChartData, setBillingChartData] = useState([]);
  const [prescriptionStats, setPrescriptionStats] = useState({
    pending: 0,
    completed: 0,
  });
  const [stockSummary, setStockSummary] = useState({
    totalBalanceQty: 0,
    below100Count: 0,
    expiringThisMonth: 0,
  });
  const [doctorData, setDoctorData] = useState({});

  const [followUpStats, setFollowUpStats] = useState({
    courtesy_call: { pending: 0, overdue: 0 },
    follow_up_1: { pending: 0, overdue: 0 },
    follow_up_2: { pending: 0, overdue: 0 },
    appointment_pending: 0,
    totalPending: 0,
    totalOverdue: 0,
  });

  const [errorMessage, setErrorMessage] = useState("");

  const setLoadingKey = (key, value) =>
    setLoading((previous) => ({ ...previous, [key]: value }));

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoadingKey("dashboard", true);
      const response = await axios.get(
        `${Apiurl}/dashboard_stats`,
        getAuthConfig()
      );
      setStats((previous) => ({
        ...previous,
        ...(response.data || {}),
      }));
    } catch (error) {
      console.error("Dashboard stats error:", error);
    } finally {
      setLoadingKey("dashboard", false);
    }
  }, []);

  const fetchPatientCount = useCallback(async () => {
    try {
      const response = await axios.get(
        `${Apiurl}/patient_count`,
        getAuthConfig()
      );
      setPatientCount(
        safeNumber(response.data?.total_patients ?? response.data?.totalPatients)
      );
    } catch (error) {
      console.error("Patient count error:", error);
      setPatientCount(0);
    }
  }, []);

  const fetchBillingData = useCallback(async () => {
    try {
      setLoadingKey("billing", true);
      const response = await axios.get(
        `${Apiurl}/billing_stats`,
        getAuthConfig()
      );
      const data = response.data || {};
      setBillingData({
        opBilling: safeNumber(data.opBilling),
        pharmacyBilling: safeNumber(data.pharmacyBilling),
        opBillCount: safeNumber(data.opBillCount),
        pharmacyBillCount: safeNumber(data.pharmacyBillCount),
      });
    } catch (error) {
      console.error("Billing data error:", error);
      setBillingData({
        opBilling: 0,
        pharmacyBilling: 0,
        opBillCount: 0,
        pharmacyBillCount: 0,
      });
    } finally {
      setLoadingKey("billing", false);
    }
  }, []);

  const fetchBillingChartData = useCallback(async (range) => {
    try {
      setLoadingKey("billingChart", true);
      const response = await axios.get(
        `${Apiurl}/dashboard_billing_chart`,
        {
          ...getAuthConfig(),
          params: { range },
        }
      );

      const rows = normaliseArray(response.data);
      setBillingChartData(
        rows.map((item) => ({
          ...item,
          label: item.label || formatDate(item.date),
          opBilling: safeNumber(item.opBilling),
          pharmacyBilling: safeNumber(item.pharmacyBilling),
        }))
      );
    } catch (error) {
      console.error("Billing chart error:", error);
      setBillingChartData([]);
    } finally {
      setLoadingKey("billingChart", false);
    }
  }, []);

  const fetchStaffOnDuty = useCallback(async () => {
    try {
      setLoadingKey("staff", true);
      const response = await axios.get(
        `${Apiurl}/attendance/on-duty`,
        getAuthConfig()
      );
      setStaffOnDuty(normaliseArray(response.data));
    } catch (error) {
      console.error("Staff on duty error:", error);
      setStaffOnDuty([]);
    } finally {
      setLoadingKey("staff", false);
    }
  }, []);

  const fetchPrescriptionStats = useCallback(async () => {
    try {
      setLoadingKey("prescriptions", true);
      const response = await axios.get(
        `${Apiurl}/prescription_stats`,
        getAuthConfig()
      );
      setPrescriptionStats({
        pending: safeNumber(response.data?.pending),
        completed: safeNumber(response.data?.completed),
      });
    } catch (error) {
      console.error("Prescription stats error:", error);
      setPrescriptionStats({ pending: 0, completed: 0 });
    } finally {
      setLoadingKey("prescriptions", false);
    }
  }, []);

  const fetchStockSummary = useCallback(async () => {
    try {
      setLoadingKey("stock", true);
      const response = await axios.get(
        `${Apiurl}/stock_pharmacy/summary`,
        getAuthConfig()
      );
      const data = response.data || {};
      setStockSummary({
        totalBalanceQty: safeNumber(data.totalBalanceQty),
        below100Count: safeNumber(data.below100Count),
        expiringThisMonth: safeNumber(data.expiringThisMonth),
      });
    } catch (error) {
      console.error("Stock summary error:", error);
      setStockSummary({
        totalBalanceQty: 0,
        below100Count: 0,
        expiringThisMonth: 0,
      });
    } finally {
      setLoadingKey("stock", false);
    }
  }, []);

  const fetchDoctorData = useCallback(async () => {
    try {
      const username = localStorage.getItem("username");
      if (!username) return;

      const response = await axios.get(
        `${Apiurl}/doctor_by_doctor_id/${username}`,
        getAuthConfig()
      );
      setDoctorData(response.data || {});
    } catch (error) {
      console.error("Doctor data error:", error);
      setDoctorData({});
    }
  }, []);

  const fetchAppointments = useCallback(
    async (filter = "today") => {
      try {
        setLoadingKey("appointments", true);

        if (isDoctor) {
          const doctorId = doctorData?._id;
          if (!doctorId) {
            setAppointmentData([]);
            return;
          }

          const response = await axios.get(
            `${Apiurl}/doctor_appointments_details/${doctorId}`,
            getAuthConfig()
          );
          const all = normaliseArray(response.data);
          const targetDate =
            filter === "yesterday"
              ? getLocalDateString(-1)
              : filter === "tomorrow"
                ? getLocalDateString(1)
                : getLocalDateString(0);

          const filtered = all
            .filter((item) => item?.appointment_date === targetDate)
            .map((item) => ({
              ...item,
              appointment_time_display: formatTo12Hour(
                item.appointment_time
              ),
              patient_details: item.patient_details || {},
              doctor_details: item.doctor_details || doctorData,
            }));

          setAppointmentData(filtered);
          return;
        }

        const response = await axios.get(
          `${Apiurl}/appointment_details_by_date`,
          {
            ...getAuthConfig(),
            params: { filter },
          }
        );

        const rows = normaliseArray(response.data);
        setAppointmentData(
          rows.map((item) => ({
            ...item,
            appointment_time_display:
              item.appointment_time_display ||
              formatTo12Hour(item.appointment_time),
          }))
        );
      } catch (error) {
        console.error("Appointments error:", error);
        setAppointmentData([]);
        setErrorMessage(
          error?.response?.data?.message || "Unable to load appointments."
        );
      } finally {
        setLoadingKey("appointments", false);
      }
    },
    [doctorData, isDoctor]
  );

  const fetchFollowUpStats = useCallback(async () => {
    if (!isManagement && !isReceptionist && !isDoctor) return;

    try {
      setLoadingKey("followups", true);

      const stages = ["courtesy_call", "follow_up_1", "follow_up_2"];

      const results = await Promise.all(
        stages.map(async (stage) => {
          const [pendingResponse, overdueResponse] = await Promise.all([
            axios.get(`${Apiurl}/follow-up-calls`, {
              ...getAuthConfig(),
              params: { stage, status: "pending" },
            }),
            axios.get(`${Apiurl}/follow-up-calls`, {
              ...getAuthConfig(),
              params: { stage, status: "overdue" },
            }),
          ]);

          const pending = normaliseArray(pendingResponse.data);
          const overdue = normaliseArray(overdueResponse.data);

          return {
            stage,
            pending,
            overdue,
          };
        })
      );

      const next = {
        courtesy_call: { pending: 0, overdue: 0 },
        follow_up_1: { pending: 0, overdue: 0 },
        follow_up_2: { pending: 0, overdue: 0 },
        appointment_pending: 0,
        totalPending: 0,
        totalOverdue: 0,
      };

      results.forEach(({ stage, pending, overdue }) => {
        const appointmentPending =
          stage === "follow_up_2"
            ? pending.filter((record) => record?.stage === "appointment_pending")
                .length
            : 0;

        next[stage] = {
          pending: pending.length,
          overdue: overdue.length,
        };

        next.appointment_pending += appointmentPending;
        next.totalPending += pending.length;
        next.totalOverdue += overdue.length;
      });

      setFollowUpStats(next);
    } catch (error) {
      console.error("Follow-up dashboard error:", error);
      setFollowUpStats({
        courtesy_call: { pending: 0, overdue: 0 },
        follow_up_1: { pending: 0, overdue: 0 },
        follow_up_2: { pending: 0, overdue: 0 },
        appointment_pending: 0,
        totalPending: 0,
        totalOverdue: 0,
      });
    } finally {
      setLoadingKey("followups", false);
    }
  }, [isDoctor, isManagement, isReceptionist]);

  const refreshAll = useCallback(async () => {
    setErrorMessage("");

    const requests = [];

    if (isManagement) {
      requests.push(
        fetchDashboardStats(),
        fetchPatientCount(),
        fetchBillingData(),
        fetchBillingChartData(billingRange),
        fetchStaffOnDuty(),
        fetchPrescriptionStats(),
        fetchStockSummary()
      );
    } else if (isPharmacist) {
      requests.push(
        fetchBillingData(),
        fetchBillingChartData(billingRange),
        fetchPrescriptionStats(),
        fetchStockSummary()
      );
    }

    if (isReceptionist) {
      requests.push(fetchDashboardStats());
    }

    if (isDoctor) {
      requests.push(fetchDoctorData());
    }

    if (isManagement || isReceptionist || isDoctor) {
      requests.push(fetchFollowUpStats());
    }

    await Promise.allSettled(requests);
  }, [
    billingRange,
    fetchBillingChartData,
    fetchBillingData,
    fetchDashboardStats,
    fetchDoctorData,
    fetchFollowUpStats,
    fetchPatientCount,
    fetchPrescriptionStats,
    fetchStaffOnDuty,
    fetchStockSummary,
    isDoctor,
    isManagement,
    isPharmacist,
    isReceptionist,
  ]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (canSeeAppointments) {
      fetchAppointments(appointmentFilter);
    }
  }, [appointmentFilter, canSeeAppointments, fetchAppointments]);


  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, appointmentFilter, itemsPerPage]);

  const filteredAppointments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return appointmentData
      .filter((item) => {
        if (!term) return true;

        const patient = getPatient(item);
        const doctor = getDoctor(item);

        const values = [
          item?.appointment_id,
          item?.appointment_date,
          item?.appointment_time,
          item?.department,
          item?.appointment_status,
          patient?.patient_id,
          patient?.first_name,
          patient?.last_name,
          patient?.mobile_no,
          doctor?.doctor_id,
          doctor?.doctor_name,
        ];

        return values
          .filter((value) => value !== null && value !== undefined)
          .join(" ")
          .toLowerCase()
          .includes(term);
      })
      .filter(
        (item) =>
          String(item?.appointment_status || "").toLowerCase().trim() !==
          "completed"
      );
  }, [appointmentData, searchTerm]);

  const totalPages = useMemo(() => {
    if (itemsPerPage === "All") return 1;
    return Math.max(
      1,
      Math.ceil(filteredAppointments.length / Number(itemsPerPage))
    );
  }, [filteredAppointments.length, itemsPerPage]);

  const currentItems = useMemo(() => {
    if (itemsPerPage === "All") return filteredAppointments;

    const size = Number(itemsPerPage);
    const start = (currentPage - 1) * size;
    return filteredAppointments.slice(start, start + size);
  }, [currentPage, filteredAppointments, itemsPerPage]);

  const pageNumbers = useMemo(() => {
    const max = 5;
    if (totalPages <= max) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const result = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + max - 1);

    if (start > 1) result.push(1, "...");
    for (let page = start; page <= end; page += 1) result.push(page);
    if (end < totalPages) result.push("...", totalPages);

    return result;
  }, [currentPage, totalPages]);

  const opBillingChart = useMemo(
    () => ({
      labels: billingChartData.map((item) => item.label),
      datasets: [
        {
          label: "O/P Billing",
          data: billingChartData.map((item) => item.opBilling),
          backgroundColor: "rgba(75,145,240,.78)",
          borderColor: COLORS.blue,
          borderWidth: 1,
          borderRadius: 7,
          maxBarThickness: 28,
        },
      ],
    }),
    [billingChartData]
  );

  const pharmacyBillingChart = useMemo(
    () => ({
      labels: billingChartData.map((item) => item.label),
      datasets: [
        {
          label: "Pharmacy Billing",
          data: billingChartData.map((item) => item.pharmacyBilling),
          backgroundColor: "rgba(121,193,67,.78)",
          borderColor: COLORS.green,
          borderWidth: 1,
          borderRadius: 7,
          maxBarThickness: 28,
        },
      ],
    }),
    [billingChartData]
  );

  const billingOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${formatMoney(context.raw)}`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: { color: "rgba(23,50,77,.07)" },
          ticks: {
            font: { size: 10 },
            callback: (value) => formatMoney(value),
          },
        },
        x: {
          grid: { display: false },
          ticks: { font: { size: 9 }, maxRotation: 0 },
        },
      },
    }),
    []
  );

  // Appointment status chart.
  // Use the complete appointmentData array so completed appointments
  // are included in the chart even though the table hides completed rows.
  const appointmentStatusCounts = useMemo(() => {
    const counts = {
      Scheduled: 0,
      "In Progress": 0,
      Completed: 0,
      Cancelled: 0,
      Other: 0,
    };

    appointmentData.forEach((item) => {
      const rawStatus = String(item?.appointment_status || "")
        .trim()
        .toLowerCase();

      if (!rawStatus) {
        counts.Other += 1;
      } else if (rawStatus === "scheduled") {
        counts.Scheduled += 1;
      } else if (
        rawStatus === "in progress" ||
        rawStatus === "in-progress" ||
        rawStatus === "in_progress"
      ) {
        counts["In Progress"] += 1;
      } else if (rawStatus === "completed") {
        counts.Completed += 1;
      } else if (
        rawStatus === "cancelled" ||
        rawStatus === "canceled"
      ) {
        counts.Cancelled += 1;
      } else {
        counts.Other += 1;
      }
    });

    return counts;
  }, [appointmentData]);

  const appointmentPieData = useMemo(
    () => ({
      labels: ["Scheduled", "In Progress", "Completed", "Cancelled", "Other"],
      datasets: [
        {
          data: [
            appointmentStatusCounts.Scheduled,
            appointmentStatusCounts["In Progress"],
            appointmentStatusCounts.Completed,
            appointmentStatusCounts.Cancelled,
            appointmentStatusCounts.Other,
          ],
          backgroundColor: [
            COLORS.blue,
            COLORS.orange,
            COLORS.green,
            COLORS.red,
            COLORS.purple,
          ],
          borderColor: "#fff",
          borderWidth: 3,
        },
      ],
    }),
    [appointmentStatusCounts]
  );

  const appointmentPieOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "bottom",
          labels: {
            font: { size: 9 },
            padding: 8,
            usePointStyle: true,
          },
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.label}: ${Number(context.raw || 0).toLocaleString(
                "en-IN"
              )}`,
          },
        },
      },
    }),
    []
  );

  const openFollowUp = (stage) => {
    const paths = {
      courtesy_call: "/follow-up-calls/courtesy-call",
      follow_up_1: "/follow-up-calls/follow-up-1",
      follow_up_2: "/follow-up-calls/follow-up-2",
    };

    navigate(paths[stage] || "/follow-up-calls");
  };

  const getStatusClass = (status) => {
    const normalized = String(status || "")
      .toLowerCase()
      .replace(/\s+/g, "-");

    if (normalized === "scheduled") return "status-scheduled";
    if (normalized === "in-progress") return "status-progress";
    if (normalized === "completed") return "status-completed";
    return "";
  };

  const displayAppointmentCount = loading.appointments
    ? "..."
    : filteredAppointments.length;

  return (
    <>
      <style>{`
        .dashboard-page {
          min-height: 100vh;
          padding: 18px;
          background:
            radial-gradient(circle at top right, rgba(1,174,190,.09), transparent 30%),
            radial-gradient(circle at top left, rgba(121,193,67,.07), transparent 25%),
            ${COLORS.bg};
        }

        .dashboard-header {
          border-radius: 20px;
          padding: 18px 20px;
          color: white;
          background: linear-gradient(135deg, ${COLORS.teal}, #087F98 55%, ${COLORS.green});
          box-shadow: 0 10px 30px rgba(1,174,190,.16);
        }

        .dashboard-kpi-card {
          transition: transform .22s ease, box-shadow .22s ease;
          box-shadow: 0 5px 20px rgba(23,50,77,.06);
        }

        .dashboard-kpi-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(23,50,77,.12);
        }

        .dashboard-kpi-modern {
          position: relative;
          overflow: hidden;
          border-radius: 18px !important;
          background:
            linear-gradient(145deg, #ffffff 0%, #fbfdfe 100%) !important;
          border: 1px solid #e8f0f2 !important;
        }

        .dashboard-kpi-modern::after {
          content: "";
          position: absolute;
          width: 78px;
          height: 78px;
          right: -26px;
          bottom: -28px;
          border-radius: 50%;
          background: var(--kpi-color);
          opacity: .055;
          pointer-events: none;
        }

        .dashboard-kpi-accent {
          position: absolute;
          left: 0;
          top: 18px;
          width: 4px;
          height: 38px;
          border-radius: 0 6px 6px 0;
        }

        .dashboard-kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .dashboard-kpi-chip {
          display: inline-flex;
          align-items: center;
          padding: 4px 7px;
          border-radius: 8px;
          font-size: 9px;
          font-weight: 700;
          white-space: nowrap;
        }

        .dashboard-section-card {
          border: 0;
          border-radius: 18px;
          box-shadow: 0 5px 20px rgba(23,50,77,.055);
          background: #fff;
        }

        .dashboard-chart-box {
          height: 245px;
          position: relative;
        }

        .dashboard-billing-chart-box {
          height: 285px;
        }

        .dashboard-billing-tabs {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px;
          border: 1px solid #d9e8e8;
          border-radius: 11px;
          background: #f5f9f9;
        }

        .dashboard-billing-tab {
          min-width: 88px;
          padding: 7px 15px !important;
          border: 0 !important;
          border-radius: 8px !important;
          background: transparent !important;
          color: #587075 !important;
          font-size: 12px !important;
          font-weight: 700 !important;
          line-height: 1.2;
          box-shadow: none !important;
          transition: background .18s ease, color .18s ease, box-shadow .18s ease;
        }

        .dashboard-billing-tab:hover {
          background: #e9f6f5 !important;
          color: ${COLORS.teal} !important;
        }

        .dashboard-billing-tab.active {
          background: ${COLORS.teal} !important;
          color: #fff !important;
          box-shadow: 0 3px 9px rgba(1,174,190,.22) !important;
        }

        .dashboard-billing-today {
          min-width: 112px;
          padding: 7px 10px;
          border-radius: 9px;
          font-size: 11px;
          font-weight: 750;
          text-align: center;
          white-space: nowrap;
        }

        .dashboard-chart-filter,
        .dashboard-select,
        .dashboard-search {
          border: 1px solid #dce6eb !important;
          border-radius: 9px !important;
          background: #fff !important;
          font-size: 12px !important;
        }

        .dashboard-search {
          width: 210px;
          padding: 7px 34px 7px 11px !important;
        }

        .dashboard-search:focus,
        .dashboard-select:focus,
        .dashboard-chart-filter:focus {
          border-color: ${COLORS.teal} !important;
          box-shadow: 0 0 0 3px rgba(1,174,190,.10) !important;
        }

        .followup-mini-card {
          border-radius: 14px;
          padding: 13px;
          background: linear-gradient(145deg, #fff, #f8fbfc);
          border: 1px solid #e7eef1;
          cursor: pointer;
          transition: all .2s ease;
        }

        .followup-mini-card:hover {
          transform: translateY(-2px);
          border-color: ${COLORS.teal};
          box-shadow: 0 8px 22px rgba(1,174,190,.10);
        }

        .appointment-table th {
          font-size: 11px;
          color: ${COLORS.muted};
          text-transform: uppercase;
          letter-spacing: .4px;
          padding: 11px 8px;
          white-space: nowrap;
          background: #f7fafb;
        }

        .appointment-table td {
          font-size: 12px;
          padding: 10px 8px;
          vertical-align: middle;
          white-space: nowrap;
        }

        .status-badge {
          display: inline-block;
          padding: 4px 9px;
          border-radius: 20px;
          font-size: 10px;
          font-weight: 700;
        }

        .status-scheduled {
          background: #e9f3ff;
          color: #1769aa;
        }

        .status-progress {
          background: #fff2dc;
          color: #b76b00;
        }

        .status-completed {
          background: #eaf8ed;
          color: #258143;
        }

        .pagination-btn {
          border: 1px solid #dce6eb;
          background: #fff;
          border-radius: 8px;
          min-width: 31px;
          height: 31px;
          font-size: 12px;
        }

        .pagination-btn.active {
          background: ${COLORS.teal};
          border-color: ${COLORS.teal};
          color: #fff;
        }

        .dashboard-tab {
          border: 0;
          border-radius: 9px;
          padding: 6px 12px;
          font-size: 11px;
          background: #f1f5f7;
          color: ${COLORS.muted};
          font-weight: 600;
        }

        .dashboard-tab.active {
          color: #fff;
          background: ${COLORS.teal};
        }

        @media (max-width: 768px) {
          .dashboard-page { padding: 10px; }
          .dashboard-search { width: 160px; }
          .dashboard-chart-box { height: 220px; }
        }
      `}</style>

      <div className="dashboard-page">
        <div className="dashboard-header mb-3">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <div
                style={{
                  fontSize: 12,
                  opacity: 0.86,
                  letterSpacing: ".5px",
                }}
              >
                4HEALTH HOSPITAL
              </div>
              <h4 className="mb-1 fw-bold">Dashboard</h4>
              <div style={{ fontSize: 12, opacity: 0.88 }}>
                Live hospital operations, billing, pharmacy and follow-up
                activity
              </div>
            </div>

            <button
              type="button"
              className="btn btn-light btn-sm px-3"
              style={{
                borderRadius: 9,
                color: COLORS.teal,
                fontWeight: 700,
              }}
              onClick={refreshAll}
            >
              Refresh Dashboard
            </button>
          </div>
        </div>

        {errorMessage ? (
          <div className="alert alert-warning py-2 small mb-3">
            {errorMessage}
          </div>
        ) : null}

        {canSeeManagement ? (
          <>
            <div className="row g-3 mb-3">
              <StatsCard
                icon={FaUserInjured}
                title="All Patients"
                value={patientCount.toLocaleString("en-IN")}
                subtitle="Total registered patients"
                color={COLORS.blue}
              />

              <BillingSummaryCard billingData={billingData} />

              <StatsCard
                icon={MdEventNote}
                title="Appointments"
                value={
                  loading.appointments
                    ? "..."
                    : appointmentData.length.toLocaleString("en-IN")
                }
                subtitle={`${appointmentFilter} appointments`}
                color={COLORS.orange}
              />

              <StatsCard
                icon={IoPeopleSharp}
                title="Staff On Duty"
                value={loading.staff ? "..." : staffOnDuty.length}
                subtitle={`${safeNumber(stats.totalStaff)} total staff`}
                color={COLORS.green}
              />
            </div>
          </>
        ) : null}

        {isDoctor ? (
          <div className="row g-3 mb-3">
            <StatsCard
              icon={FaUserMd}
              title="Doctor ID"
              value={doctorData.doctor_id || "N/A"}
              subtitle="Logged-in doctor"
              color={COLORS.blue}
            />
            <StatsCard
              icon={FaUserMd}
              title="Doctor"
              value={doctorData.doctor_name || "N/A"}
              subtitle={doctorData.department || "Department not available"}
              color={COLORS.teal}
            />
            <StatsCard
              icon={FaCalendarAlt}
              title="Appointments"
              value={displayAppointmentCount}
              subtitle={`${appointmentFilter} active appointments`}
              color={COLORS.orange}
            />
            <StatsCard
              icon={FaClock}
              title="Follow-ups"
              value={followUpStats.totalPending}
              subtitle={`${followUpStats.totalOverdue} overdue`}
              color={COLORS.red}
              onClick={() => openFollowUp("follow_up_1")}
              buttonText="Open Follow-ups"
            />
          </div>
        ) : null}

        {/* {canSeePharmacy ? (
          <div className="row g-3 mb-3">
            <StatsCard
              icon={FaPills}
              title="Medicine In Stock"
              value={safeNumber(stockSummary.totalBalanceQty).toLocaleString(
                "en-IN"
              )}
              subtitle={`${stockSummary.below100Count} low-stock items`}
              color={COLORS.blue}
            />
            <StatsCard
              icon={FaClock}
              title="Expiring Soon"
              value={stockSummary.expiringThisMonth}
              subtitle="Medicines expiring this month"
              color={COLORS.orange}
            />
            <StatsCard
              icon={FaPrescription}
              title="Prescriptions"
              value={
                prescriptionStats.pending + prescriptionStats.completed
              }
              subtitle={`${prescriptionStats.pending} pending / ${prescriptionStats.completed} completed`}
              color={COLORS.purple}
            />
            <StatsCard
              icon={MdLocalPharmacy}
              title="Pharmacy Sales"
              value={billingData.pharmacyBillCount}
              subtitle={`${formatMoney(
                billingData.pharmacyBilling
              )} billed today`}
              color={COLORS.green}
            />
          </div>
        ) : null} */}

        {(isManagement || isReceptionist || isDoctor) ? (
          <div className="dashboard-section-card mb-3">
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h6
                    className="mb-1 fw-bold"
                    style={{ color: COLORS.dark }}
                  >
                    <FaPhoneAlt
                      className="me-2"
                      style={{ color: COLORS.teal }}
                    />
                    Follow-up Management
                  </h6>
                  <small style={{ color: COLORS.muted }}>
                    Courtesy Call → Follow-up 1 → Follow-up 2 → Next Visit
                    Appointment
                  </small>
                </div>

                <button
                  type="button"
                  className="dashboard-tab active"
                  onClick={() => navigate("/follow-up-calls")}
                >
                  Open Follow-up Module
                </button>
              </div>

              <div className="row g-2">
                {[
                  {
                    stage: "courtesy_call",
                    title: "Courtesy Call",
                    color: COLORS.blue,
                    icon: "1",
                  },
                  {
                    stage: "follow_up_1",
                    title: "Follow-up 1",
                    color: COLORS.teal,
                    icon: "2",
                  },
                  {
                    stage: "follow_up_2",
                    title: "Follow-up 2",
                    color: COLORS.green,
                    icon: "3",
                  },
                ].map((item) => (
                  <div className="col-12 col-md-4" key={item.stage}>
                    <div
                      className="followup-mini-card"
                      onClick={() => openFollowUp(item.stage)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          openFollowUp(item.stage);
                        }
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 32,
                              height: 32,
                              background: `${item.color}18`,
                              color: item.color,
                              fontWeight: 800,
                            }}
                          >
                            {item.icon}
                          </div>
                          <div>
                            <div
                              className="fw-bold"
                              style={{ fontSize: 12, color: COLORS.dark }}
                            >
                              {item.title}
                            </div>
                            <small style={{ color: COLORS.muted }}>
                              {followUpStats[item.stage].overdue} overdue
                            </small>
                          </div>
                        </div>

                        <div className="text-end">
                          <div
                            className="fw-bold"
                            style={{ fontSize: 21, color: item.color }}
                          >
                            {loading.followups
                              ? "..."
                              : followUpStats[item.stage].pending}
                          </div>
                          <small style={{ color: COLORS.muted }}>pending</small>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div
                className="d-flex flex-wrap gap-3 mt-3 pt-3 border-top"
                style={{ fontSize: 12 }}
              >
                <span>
                  <strong style={{ color: COLORS.teal }}>
                    {followUpStats.totalPending}
                  </strong>{" "}
                  total pending
                </span>
                <span>
                  <strong style={{ color: COLORS.red }}>
                    {followUpStats.totalOverdue}
                  </strong>{" "}
                  overdue
                </span>
                <span>
                  <strong style={{ color: COLORS.orange }}>
                    {followUpStats.appointment_pending}
                  </strong>{" "}
                  next appointments pending
                </span>
              </div>
            </div>
          </div>
        ) : null}

        {canSeeManagement || isPharmacist ? (
          <div className="row g-3 mb-3">
            <div className="col-12 col-lg-6">
              <div className="dashboard-section-card h-100">
                <div className="card-body p-3">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                    <div>
                      <h6 className="mb-1 fw-bold" style={{ color: COLORS.dark }}>
                        Billing Overview
                      </h6>
                      <small style={{ color: COLORS.muted }}>
                        {billingTab === "op"
                          ? "Daily outpatient billing"
                          : "Daily pharmacy sales billing"}
                      </small>
                    </div>

                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <div
                        className="dashboard-billing-tabs"
                        role="tablist"
                        aria-label="Billing chart type"
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={billingTab === "op"}
                          className={`dashboard-billing-tab ${
                            billingTab === "op" ? "active" : ""
                          }`}
                          onClick={() => setBillingTab("op")}
                        >
                          O/P
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={billingTab === "pharmacy"}
                          className={`dashboard-billing-tab ${
                            billingTab === "pharmacy" ? "active" : ""
                          }`}
                          onClick={() => setBillingTab("pharmacy")}
                        >
                          Pharmacy
                        </button>
                      </div>

                      <select
                        className="form-select form-select-sm dashboard-chart-filter"
                        style={{ width: 110 }}
                        value={billingRange}
                        onChange={(event) => {
                          const value = event.target.value;
                          setBillingRange(value);
                          fetchBillingChartData(value);
                        }}
                      >
                        <option value="last7days">7 Days</option>
                        <option value="last30days">30 Days</option>
                      </select>

                      <div
                        className="dashboard-billing-today"
                        style={{
                          background:
                            billingTab === "op"
                              ? `${COLORS.blue}14`
                              : `${COLORS.green}14`,
                          color:
                            billingTab === "op"
                              ? COLORS.blue
                              : COLORS.green,
                        }}
                      >
                        {formatMoney(
                          billingTab === "op"
                            ? billingData.opBilling
                            : billingData.pharmacyBilling
                        )}{" "}
                        today
                      </div>
                    </div>
                  </div>

                  <div className="dashboard-chart-box dashboard-billing-chart-box">
                    {loading.billingChart ? (
                      <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                        Loading billing...
                      </div>
                    ) : (
                      <Bar
                        key={billingTab}
                        data={
                          billingTab === "op"
                            ? opBillingChart
                            : pharmacyBillingChart
                        }
                        options={billingOptions}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="dashboard-section-card h-100">
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-1 fw-bold" style={{ color: COLORS.dark }}>
                        Appointment Status
                      </h6>
                      <small style={{ color: COLORS.muted }}>
                        {appointmentFilter === "today"
                          ? "Today's appointment status"
                          : `${appointmentFilter.charAt(0).toUpperCase()}${appointmentFilter.slice(1)} appointment status`}
                      </small>
                    </div>
                    <FaCalendarAlt size={20} color={COLORS.teal} />
                  </div>

                  <div
                    className="d-flex gap-1 mb-2"
                    style={{ justifyContent: "flex-end" }}
                  >
                    {["yesterday", "today", "tomorrow"].map((day) => (
                      <button
                        type="button"
                        key={`chart-${day}`}
                        className={`dashboard-tab ${
                          appointmentFilter === day ? "active" : ""
                        }`}
                        onClick={() => setAppointmentFilter(day)}
                      >
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </button>
                    ))}
                  </div>

                  <div className="dashboard-chart-box dashboard-billing-chart-box">
                    {loading.appointments ? (
                      <div className="h-100 d-flex align-items-center justify-content-center text-muted small">
                        Loading appointments...
                      </div>
                    ) : appointmentData.length > 0 ? (
                      <Pie
                        data={appointmentPieData}
                        options={appointmentPieOptions}
                      />
                    ) : (
                      <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted">
                        <FaCalendarAlt size={28} className="mb-2 opacity-50" />
                        <small>No appointments for {appointmentFilter}</small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {canSeePharmacy ? (
          <div className="row g-3 mb-3">
            <div className="col-12 col-lg-7">
              <div className="dashboard-section-card h-100">
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-1 fw-bold" style={{ color: COLORS.dark }}>
                        Pharmacy Snapshot
                      </h6>
                      <small style={{ color: COLORS.muted }}>
                        Current stock and sales indicators
                      </small>
                    </div>
                    <MdLocalPharmacy size={25} color={COLORS.green} />
                  </div>

                  <div className="row g-2">
                    <div className="col-12 col-md-4">
                      <div
                        className="p-3 rounded-4 h-100"
                        style={{ background: `${COLORS.blue}10` }}
                      >
                        <small style={{ color: COLORS.muted }}>Stock Qty</small>
                        <div
                          className="fw-bold mt-1"
                          style={{ color: COLORS.blue, fontSize: 23 }}
                        >
                          {stockSummary.totalBalanceQty.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div
                        className="p-3 rounded-4 h-100"
                        style={{ background: `${COLORS.orange}12` }}
                      >
                        <small style={{ color: COLORS.muted }}>Low Stock</small>
                        <div
                          className="fw-bold mt-1"
                          style={{ color: COLORS.orange, fontSize: 23 }}
                        >
                          {stockSummary.below100Count}
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-4">
                      <div
                        className="p-3 rounded-4 h-100"
                        style={{ background: `${COLORS.green}12` }}
                      >
                        <small style={{ color: COLORS.muted }}>Sales Today</small>
                        <div
                          className="fw-bold mt-1"
                          style={{ color: COLORS.green, fontSize: 20 }}
                        >
                          {formatMoney(billingData.pharmacyBilling)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-5">
              <div className="dashboard-section-card h-100">
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h6 className="mb-1 fw-bold" style={{ color: COLORS.dark }}>
                        Prescription Overview
                      </h6>
                      <small style={{ color: COLORS.muted }}>
                        Today's pharmacy prescription activity
                      </small>
                    </div>

                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: 42,
                        height: 42,
                        background: `${COLORS.purple}12`,
                        color: COLORS.purple,
                      }}
                    >
                      <FaPrescription size={20} />
                    </div>
                  </div>

                  <div className="row g-2">
                    <div className="col-6">
                      <div
                        className="p-3 rounded-4 h-100"
                        style={{
                          background: `${COLORS.orange}10`,
                          border: `1px solid ${COLORS.orange}18`,
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <small style={{ color: COLORS.muted, fontWeight: 600 }}>
                            Pending
                          </small>
                          <FaClock size={14} color={COLORS.orange} />
                        </div>

                        <div
                          className="fw-bold"
                          style={{ color: COLORS.orange, fontSize: 25 }}
                        >
                          {loading.prescriptions
                            ? "..."
                            : prescriptionStats.pending}
                        </div>

                        <small style={{ color: COLORS.muted }}>
                          Awaiting pharmacy
                        </small>
                      </div>
                    </div>

                    <div className="col-6">
                      <div
                        className="p-3 rounded-4 h-100"
                        style={{
                          background: `${COLORS.green}10`,
                          border: `1px solid ${COLORS.green}18`,
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <small style={{ color: COLORS.muted, fontWeight: 600 }}>
                            Completed
                          </small>
                          <FaPrescription size={14} color={COLORS.green} />
                        </div>

                        <div
                          className="fw-bold"
                          style={{ color: COLORS.green, fontSize: 25 }}
                        >
                          {loading.prescriptions
                            ? "..."
                            : prescriptionStats.completed}
                        </div>

                        <small style={{ color: COLORS.muted }}>
                          Processed successfully
                        </small>
                      </div>
                    </div>
                  </div>

                  <div
                    className="d-flex justify-content-between align-items-center mt-2 px-2 py-2 rounded-3"
                    style={{ background: `${COLORS.purple}08` }}
                  >
                    <small style={{ color: COLORS.muted, fontWeight: 600 }}>
                      Total Prescriptions
                    </small>
                    <span
                      className="fw-bold"
                      style={{ color: COLORS.purple, fontSize: 16 }}
                    >
                      {loading.prescriptions
                        ? "..."
                        : safeNumber(prescriptionStats.pending) +
                          safeNumber(prescriptionStats.completed)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {canSeeAppointments ? (
          <div className="dashboard-section-card">
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h6 className="mb-1 fw-bold" style={{ color: COLORS.dark }}>
                    <FaCalendarAlt
                      className="me-2"
                      style={{ color: COLORS.teal }}
                    />
                    Appointments
                  </h6>
                  <small style={{ color: COLORS.muted }}>
                    {appointmentData.length} appointments loaded for{" "}
                    {appointmentFilter}
                  </small>
                </div>

                <div className="d-flex gap-1">
                  {["yesterday", "today", "tomorrow"].map((day) => (
                    <button
                      type="button"
                      key={day}
                      className={`dashboard-tab ${
                        appointmentFilter === day ? "active" : ""
                      }`}
                      onClick={() => setAppointmentFilter(day)}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="d-flex align-items-center gap-2">
                  <small style={{ color: COLORS.muted }}>Show</small>
                  <select
                    className="form-select form-select-sm dashboard-select"
                    style={{ width: 85 }}
                    value={itemsPerPage}
                    onChange={(event) => {
                      const value = event.target.value;
                      setItemsPerPage(value === "All" ? "All" : Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value="All">All</option>
                    {[5, 10, 20, 50, 100].map((number) => (
                      <option key={number} value={number}>
                        {number}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control form-control-sm dashboard-search"
                    placeholder="Search patient, ID, doctor..."
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <FaSearch
                    size={12}
                    className="position-absolute"
                    style={{
                      right: 11,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: COLORS.muted,
                    }}
                  />
                </div>
              </div>

              <div className="table-responsive">
                <table className="table mb-0 appointment-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Patient ID</th>
                      <th>Patient</th>
                      <th>Doctor</th>
                      <th>Department</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {loading.appointments ? (
                      <tr>
                        <td colSpan={8} className="text-center py-5 text-muted">
                          Loading appointments...
                        </td>
                      </tr>
                    ) : currentItems.length ? (
                      currentItems.map((item, index) => (
                        <tr
                          key={item?._id || item?.appointment_id || index}
                          style={{ borderBottom: "1px solid #eef3f5" }}
                        >
                          <td>
                            {(currentPage - 1) *
                              (itemsPerPage === "All"
                                ? filteredAppointments.length
                                : Number(itemsPerPage)) +
                              index +
                              1}
                          </td>
                          <td>
                            <span
                              className="fw-bold"
                              style={{ color: COLORS.teal }}
                            >
                              {getPatientId(item)}
                            </span>
                          </td>
                          <td className="fw-semibold">{getPatientName(item)}</td>
                          <td>{getDoctorName(item)}</td>
                          <td>{item?.department || "N/A"}</td>
                          <td>{formatDate(item?.appointment_date)}</td>
                          <td>
                            {item?.appointment_time_display ||
                              formatTo12Hour(item?.appointment_time)}
                          </td>
                          <td>
                            <span
                              className={`status-badge ${getStatusClass(
                                item?.appointment_status
                              )}`}
                            >
                              {item?.appointment_status || "N/A"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={8} className="text-center py-5 text-muted">
                          {searchTerm
                            ? "No matching appointments found."
                            : "No appointments found for this date."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {filteredAppointments.length > 0 && itemsPerPage !== "All" ? (
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 pt-3 mt-2 border-top">
                  <small style={{ color: COLORS.muted }}>
                    Showing{" "}
                    {(currentPage - 1) * Number(itemsPerPage) + 1}-
                    {Math.min(
                      currentPage * Number(itemsPerPage),
                      filteredAppointments.length
                    )}{" "}
                    of {filteredAppointments.length}
                  </small>

                  <div className="d-flex gap-1">
                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={currentPage === 1}
                      onClick={() =>
                        setCurrentPage((page) => Math.max(1, page - 1))
                      }
                    >
                      ‹
                    </button>

                    {pageNumbers.map((page, index) => (
                      <button
                        type="button"
                        key={`${page}-${index}`}
                        className={`pagination-btn ${
                          page === currentPage ? "active" : ""
                        }`}
                        disabled={page === "..."}
                        onClick={() => {
                          if (page !== "...") setCurrentPage(page);
                        }}
                      >
                        {page}
                      </button>
                    ))}

                    <button
                      type="button"
                      className="pagination-btn"
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((page) =>
                          Math.min(totalPages, page + 1)
                        )
                      }
                    >
                      ›
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Dashboard;